// src/components/CookieConsentBanner.js
import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { ConsentContext } from '../context/ConsentContext';
import '../assets/styles/CookieConsentBanner.css';

/**
 * Shows until a consent record exists (guest or user), then hides permanently.
 * This banner should NOT show if user has already given consent (either via this banner or consent gate).
 */
export default function CookieConsentBanner() {
  const { consent, loading, submitConsent } = useContext(ConsentContext);
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Show banner when loaded and consent is required
  // Check multiple sources to determine if consent has been given
  useEffect(() => {
    if (loading) return;
    
    // Check if user has already given consent
    // Check 1: Consent context data (from API)
    const hasConsentFromContext = consent && 
      typeof consent === 'object' && 
      consent.has_consent === true && 
      consent.consent_required !== true;
    
    // Check 2: Cookie (fallback check)
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('bloompal_consent='))
      ?.split('=')[1];
    const hasConsentFromCookie = cookieValue === 'true';
    
    // Check 3: localStorage cache (additional fallback)
    let hasConsentFromCache = false;
    try {
      const cached = localStorage.getItem('consent_cached');
      if (cached) {
        const parsed = JSON.parse(cached);
        hasConsentFromCache = parsed?.has_consent === true && parsed?.consent_required !== true;
      }
    } catch (e) {
      // Ignore parse errors
    }
    
    // Check 4: localStorage flag (set when user clicks accept/reject)
    let hasConsentFromFlag = false;
    try {
      hasConsentFromFlag = localStorage.getItem('cookie_consent_given') === 'true';
    } catch (e) {
      // Ignore localStorage errors
    }
    
    // If ANY source indicates consent has been given, hide the banner
    const hasConsent = hasConsentFromContext || hasConsentFromCookie || hasConsentFromCache || hasConsentFromFlag;
    
    // Only show banner if consent is NOT given
    setVisible(!hasConsent);
  }, [loading, consent]);

  // Handle both accept & reject
  const handleConsent = async (options) => {
    // Prevent duplicate submissions
    if (submitting) return;
    
    setSubmitting(true);
    const timestamp = dayjs().toISOString();
    
    try {
      const result = await submitConsent({
        consents: options,
        timestamp,
        appVersion: process.env.REACT_APP_VERSION || '1.0.0',
        version: '2025.1',
      });
      
      // submitConsent already updates cache, but ensure flags are set
      localStorage.setItem('cookie_consent_given', 'true');
      if (result) {
        localStorage.setItem('consent_cached', JSON.stringify({ ...result, has_consent: true, consent_required: false }));
      }
      
      // Hide banner after successful submission
      setVisible(false);
    } catch (err) {
      console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
      // Don't hide banner on error - let user try again
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="cookie-banner">
      <p>
        BloomPal uses cookies for essential functionality and optional analytics & personalization.&nbsp;
        <Link to="/privacy-policy" className="privacy-link">Privacy Policy</Link>
      </p>
      <div className="cookie-actions">
        <button
          className="btn reject"
          onClick={() => handleConsent({
            essential: true,
            treatment: true,
            analytics: false,
            communications: false,
            research: false,
            providerSharing: false,
            cookies: false,
          })}
          disabled={submitting}
        >
          {submitting ? 'Processing...' : 'Reject Optional'}
        </button>
        <button
          className="btn accept"
          onClick={() => handleConsent({
            essential: true,
            treatment: true,
            analytics: true,
            communications: true,
            research: true,
            providerSharing: true,
            cookies: true,
          })}
          disabled={submitting}
        >
          {submitting ? 'Processing...' : 'Accept All'}
        </button>
      </div>
    </div>
  );
}